/**
 * @file MappingContent.js
 * @description Composant principal pour l'édition de mappings.
 *
 * Ce composant permet de:
 * - Transformer un mapping string (avec variables handlebars) en interface visuelle
 * - Gérer les correspondances entre chemins de sortie et valeurs d'entrée
 * - Supporter deux modes: objet (clé-valeur) et valeur simple
 *
 * Architecture du mapping:
 * 1. Le mapping original (string) contient des variables handlebars: {{{ json context.user }}}
 * 2. Ces variables sont remplacées par des UUIDs pour faciliter le parsing JSON
 * 3. Un mapObject stocke la correspondance UUID -> expression handlebars originale
 * 4. Lors de la reconstruction, les UUIDs sont remplacés par les expressions originales
 *
 * @example
 * // Mapping objet: { "userName": "{{{ json context.user.name }}}" }
 * // Mapping valeur: {{{ json context.user }}}
 */
import "./style.css";
import lod_ from "lodash";
import { v4 } from "uuid";

import { Icon, IconButton, InputAdornment, Tooltip } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import { queryDictionaries } from "helpers/dictionary";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { t } from "i18next";
import MDInput from "components/Basics/MDInput";
import MDButton from "components/Basics/MDButton";
import RowMapping, { TYPES } from "./rowMapping";

/**
 * Composant d'édition de mapping visuel.
 *
 * @param {Object} props
 * @param {number} props.reload - Compteur de rechargement pour forcer la réinitialisation
 * @param {string} props.mapping - Mapping string initial (avec handlebars)
 * @param {Object} props.config - Configuration contenant les dictionnaires disponibles
 * @param {Function} props.handleChangeMapping - Callback appelé à chaque modification du mapping
 * @param {boolean} props.mappingIsObject - Indique si le mapping est un objet ou une valeur simple
 * @param {Function} props.setMappingIsObject - Setter pour changer le mode objet/valeur
 */
const MappingContent = ({
	reload,
	mapping: mappingProps,
	config,
	handleChangeMapping,
	mappingIsObject,
	setMappingIsObject
}) => {
	/*
	 * ARCHITECTURE DU MAPPING:
	 * ========================
	 *
	 * Le mapping peut être de deux types:
	 * - Mode OBJET: { "key1": "{{{ json context.user }}}" }
	 * - Mode VALEUR: {{{ json context.user }}}
	 *
	 * Pour manipuler le mapping en JSON, on remplace les expressions handlebars
	 * par des UUIDs temporaires, puis on les restaure lors de la reconstruction.
	 *
	 * Flux de données:
	 * mappingProps (string) -> createMappingValuesFromString() -> mapping (JSON) + mapObject (UUID->HBS)
	 * mapping + mapObject -> buildMappingStringFromObject() -> mappingProps reconstruit
	 */

	/** Référence vers le bas de la liste pour le scroll automatique */
	const bottom = useRef(null);

	/**
	 * Mapping JSON avec les UUIDs à la place des expressions handlebars.
	 * Permet de manipuler le mapping comme un objet JSON valide.
	 * @type {string|null} - Mapping sérialisé en JSON
	 */
	const [mapping, setMapping] = useState(null);

	/**
	 * Table de correspondance UUID -> expression handlebars originale.
	 * @type {Object.<string, string>} - { uuid: "{{{ json path }}}" }
	 */
	const [mapObject, setMapObject] = useState({});

	/** État de chargement initial */
	const [loading, setLoading] = useState(true);

	/** Mode édition manuelle du mapping */
	const [isEditing, setIsEditing] = useState(false);

	/** Valeur en cours d'édition */
	const [editedValue, setEditedValue] = useState("");

	/**
	 * Liste tous les chemins possibles dans un objet de mapping.
	 *
	 * Parcourt récursivement l'objet et retourne tous les chemins
	 * vers les valeurs feuilles (non-objets).
	 *
	 * @example
	 * getAllPaths({ user: { name: "John", age: 30 } })
	 * // Retourne: ["user.name", "user.age"]
	 *
	 * @param {Object} obj - Objet à parcourir
	 * @param {string} currentPath - Chemin courant (pour la récursion)
	 * @returns {string[]} Liste des chemins vers les valeurs feuilles
	 */
	const getAllPaths = useCallback((obj, currentPath = "") => {
		return lod_.flatMap(lod_.keys(obj), key => {
			const newPath = currentPath ? `${currentPath}.${key}` : key;
			if (lod_.isObject(obj[key]) && !lod_.isArray(obj[key])) {
				return getAllPaths(obj[key], newPath);
			}
			return newPath;
		});
	}, []);

	/**
	 * Transforme un mapping string en mapping JSON manipulable.
	 *
	 * Remplace toutes les expressions handlebars ({{ }} ou {{{ }}}) par des UUIDs
	 * pour permettre le parsing JSON. Stocke la correspondance dans mapObject.
	 *
	 * @example
	 * Input:  '{ "name": "{{{ json context.user }}}" }'
	 * Output: mapping = '{ "name": "uuid-123" }'
	 *         mapObject = { "uuid-123": "{{{ json context.user }}}" }
	 *
	 * @param {string} mappingString - Mapping original avec expressions handlebars
	 */
	const createMappingValuesFromString = useCallback(mappingString => {
		try {
			// Regex pour capturer les expressions handlebars (2 ou 3 accolades)
			const regexHandlebars = /\{{2,3}[\s\S]*?\}{2,3}/g;
			const matches = mappingString.match(regexHandlebars);

			// Remplace chaque expression par un UUID et stocke la correspondance
			const mapUUIDLocal = {};
			let processedString = mappingString;

			if (lod_.isNil(matches) === false) {
				matches.forEach(match => {
					const uuid = v4();
					mapUUIDLocal[uuid] = match;
					processedString = processedString.replace(match, `"${uuid}"`);
				});
			}

			setMapObject(mapUUIDLocal);
			setMapping(processedString);
		} catch {
			// En cas d'erreur de parsing, on ne fait rien
			// Le mapping reste dans son état précédent
		}
	}, []);

	/**
	 * Reconstruit le mapping string original à partir du JSON et de la table de correspondance.
	 *
	 * Remplace les UUIDs par leurs expressions handlebars originales,
	 * et gère le mode valeur simple (suppression de l'enveloppe { "variable": ... }).
	 *
	 * @example
	 * Input:  mappingObject = { "uuid-123": "{{{ json context.user }}}" }
	 *         mappingString = '{ "name": "uuid-123" }'
	 * Output: '{ "name":  {{{ json context.user }}}  }'
	 *
	 * @param {Object} mappingObject - Table de correspondance UUID -> expression
	 * @param {string} mappingString - Mapping JSON avec les UUIDs
	 * @returns {string} Mapping reconstruit avec les expressions handlebars
	 */
	const buildMappingStringFromObject = useCallback(
		(mappingObject, mappingString) => {
			let mappingCopy = lod_.cloneDeep(mappingString);

			// Remplace chaque UUID par son expression handlebars originale
			Object.entries(mappingObject).forEach(([uuid, originalValue]) => {
				mappingCopy = mappingCopy.replace(`"${uuid}"`, ` ${originalValue} `);
			});

			// En mode objet, on retourne directement le résultat
			if (mappingIsObject === true) {
				return mappingCopy;
			}

			// En mode valeur simple, on supprime l'enveloppe { "variable": ... }
			mappingCopy = mappingCopy?.trim();
			// Supprime le préfixe { "variable":
			mappingCopy = mappingCopy?.replace(/\{\s*"variable"\s*:\s*/, "");
			// Supprime l'accolade fermante finale
			mappingCopy = mappingCopy?.slice(0, -1);
			return mappingCopy?.trim();
		},
		[mappingIsObject]
	);

	/**
	 * Gère le changement de chemin d'une propriété du mapping.
	 *
	 * Préserve l'ordre des propriétés lors du renommage.
	 * Si le nouveau chemin existe déjà, ajoute un suffixe UUID pour éviter les conflits.
	 *
	 * @param {string} oldPath - Ancien chemin de la propriété
	 * @param {string} newPath - Nouveau chemin souhaité
	 * @param {*} value - Valeur associée à conserver
	 */
	const onChangePath = useCallback(
		(oldPath, newPath, value) => {
			// Pas de changement si le chemin est identique
			if (oldPath === newPath) {
				return;
			}

			const mappingObjectParsed = JSON.parse(mapping);

			// Récupère tous les chemins avec leurs valeurs dans l'ordre
			const allPaths = getAllPaths(mappingObjectParsed);
			const pathValuePairs = allPaths.map(path => ({
				path,
				value: lod_.get(mappingObjectParsed, path)
			}));

			// Trouve l'index de l'ancien chemin pour le remplacer à la même position
			const oldPathIndex = pathValuePairs.findIndex(pair => pair.path === oldPath);

			if (oldPathIndex !== -1) {
				// Vérifie si le nouveau chemin existe déjà
				const newPathExists = pathValuePairs.some(pair => pair.path === newPath);

				if (newPathExists === false) {
					pathValuePairs[oldPathIndex] = { path: newPath, value };
				} else {
					// Si le chemin existe, ajoute un suffixe UUID pour éviter le conflit
					pathValuePairs[oldPathIndex] = { path: `${newPath}.${v4()}`, value };
				}
			}

			// Reconstruit l'objet à partir des paires chemin-valeur dans l'ordre
			const newMappingObject = {};
			pathValuePairs.forEach(({ path, value: val }) => {
				lod_.set(newMappingObject, path, val);
			});

			setMapping(JSON.stringify(newMappingObject));
		},
		[mapping, getAllPaths]
	);
	/**
	 * Gère le changement de valeur d'une propriété du mapping.
	 *
	 * Comportement selon le type:
	 * - DICTIONARY/HANDLEBARS: Stocke l'expression dans mapObject avec un UUID
	 * - HARDCODED/CUSTOM_VALUE: Stocke la valeur directement dans le mapping
	 *
	 * @param {string} type - Type de valeur (TYPES.DICTIONARY, TYPES.HANDLEBARS, TYPES.HARDCODED)
	 * @param {string} path - Chemin de la propriété à modifier
	 * @param {*} value - Nouvelle valeur
	 */
	const onChangeValue = useCallback(
		(type, path, value) => {
			switch (type) {
				case TYPES.DICTIONARY:
				case TYPES.HANDLEBARS: {
					// Réutilise l'UUID existant ou en génère un nouveau
					let uuid = lod_.get(JSON.parse(mapping), path);
					if (lod_.isNil(uuid?.trim()) || lod_.isEmpty(uuid?.trim())) {
						uuid = v4();
					}

					// Met à jour la table de correspondance UUID -> expression
					setMapObject(prev => {
						const newMap = lod_.cloneDeep(prev);
						lod_.set(newMap, uuid, value || "{}");
						return newMap;
					});

					// Met à jour le mapping avec l'UUID
					setMapping(prev => {
						const newMapping = lod_.cloneDeep(JSON.parse(prev));
						lod_.set(newMapping, path, uuid);
						return JSON.stringify(newMapping);
					});
					break;
				}

				case TYPES.HARDCODED:
				case TYPES.CUSTOM_VALUE:
				default: {
					// Valeur en dur: stockage direct dans le mapping
					setMapping(prev => {
						const newMapping = lod_.cloneDeep(JSON.parse(prev));
						lod_.set(newMapping, path, value);
						return JSON.stringify(newMapping);
					});
					break;
				}
			}
		},
		[mapping]
	);

	/**
	 * Supprime récursivement les objets vides d'un objet.
	 *
	 * Utilisé après la suppression d'une propriété pour nettoyer
	 * les objets parents devenus vides.
	 *
	 * @example
	 * removeEmptyObjects({ a: { b: {} }, c: 1 })
	 * // Retourne: { c: 1 }
	 *
	 * @param {*} obj - Objet à nettoyer
	 * @returns {*} Objet nettoyé
	 */
	const removeEmptyObjects = useCallback(obj => {
		if (lod_.isObject(obj) && lod_.isArray(obj) === false) {
			const filtered = lod_.mapValues(obj, removeEmptyObjects);
			return lod_.omitBy(filtered, e => lod_.isObject(e) && lod_.isEmpty(e));
		}
		return obj;
	}, []);

	/**
	 * Supprime une ligne du mapping.
	 *
	 * Supprime la propriété au chemin spécifié et nettoie les objets vides résultants.
	 *
	 * @param {string} path - Chemin de la propriété à supprimer
	 */
	const deleteRow = useCallback(
		path => {
			setMapping(prev => {
				const newMapping = lod_.cloneDeep(JSON.parse(prev));
				lod_.unset(newMapping, path);
				return JSON.stringify(removeEmptyObjects(newMapping));
			});
		},
		[removeEmptyObjects]
	);

	/**
	 * Ajoute une nouvelle ligne vide au mapping.
	 * Scroll automatiquement vers le bas après l'ajout.
	 */
	const handleAddRow = useCallback(() => {
		const mappingObjectParsed = JSON.parse(mapping);
		// Ajoute une propriété avec clé et valeur vides
		mappingObjectParsed[""] = "";
		setMapping(JSON.stringify(mappingObjectParsed));

		// Scroll vers le bas après un court délai pour laisser le DOM se mettre à jour
		setTimeout(() => {
			bottom.current?.scrollIntoView({ behavior: "smooth" });
		}, 100);
	}, [mapping]);

	/**
	 * Copie le mapping reconstruit dans le presse-papiers.
	 */
	const handleCopyToClipboard = useCallback(() => {
		const mappingString = buildMappingStringFromObject(mapObject, mapping);
		navigator.clipboard.writeText(mappingString);
	}, [buildMappingStringFromObject, mapObject, mapping]);

	/**
	 * Active le mode édition manuelle.
	 */
	const handleStartEdit = useCallback(() => {
		const mappingString = buildMappingStringFromObject(mapObject, mapping);
		setEditedValue(mappingString);
		setIsEditing(true);
	}, [buildMappingStringFromObject, mapObject, mapping]);

	/**
	 * Valide l'édition manuelle et reconstruit le mapping.
	 */
	const handleSaveEdit = useCallback(() => {
		createMappingValuesFromString(editedValue);
		setIsEditing(false);
	}, [editedValue, createMappingValuesFromString]);

	/**
	 * Annule l'édition manuelle.
	 */
	const handleCancelEdit = useCallback(() => {
		setIsEditing(false);
		setEditedValue("");
	}, []);

	/**
	 * Effet de rechargement: parse le mapping initial et détermine le mode.
	 *
	 * Détecte si le mapping est:
	 * - Un objet JSON: { "key": "value" }
	 * - Une valeur simple/handlebars: {{{ json context.user }}}
	 *
	 * En mode valeur simple, on enveloppe dans { "variable": ... } pour
	 * le manipuler comme un objet.
	 */
	useEffect(() => {
		setLoading(true);
		let mappingLocal = lod_.cloneDeep(mappingProps)?.trim() || "";

		// Détecte si c'est une valeur simple (pas un objet JSON)
		// Cas: expression handlebars ou valeur ne commençant/terminant pas par {}
		const isSimpleValue =
			(mappingLocal.startsWith("{{") && mappingLocal.endsWith("}}")) ||
			!mappingLocal.startsWith("{") ||
			!mappingLocal.endsWith("}");

		if (isSimpleValue === true) {
			setMappingIsObject(false);

			// Enveloppe dans un objet pour pouvoir le manipuler
			if (lod_.isNil(mappingLocal) || lod_.isEmpty(mappingLocal)) {
				mappingLocal = `""`;
			}
			mappingLocal = `{ "variable": ${mappingLocal} }`;
		} else {
			setMappingIsObject(true);
		}

		createMappingValuesFromString(mappingLocal);
		setLoading(false);
	}, [reload, setMappingIsObject, createMappingValuesFromString]);

	/**
	 * Effet de propagation: notifie le parent à chaque modification du mapping.
	 */
	useEffect(() => {
		if (lod_.isNil(mapping) === false) {
			handleChangeMapping(buildMappingStringFromObject(mapObject, mapping));
		}
	}, [mapping, mapObject, handleChangeMapping, buildMappingStringFromObject]);

	/**
	 * Rendu mémoïsé des lignes de mapping.
	 *
	 * Chaque ligne représente une correspondance chemin -> valeur.
	 * La clé utilise le chemin pour éviter les problèmes de réordonnancement.
	 */
	const renderRows = useMemo(() => {
		if (lod_.isNil(mapping) || lod_.isEmpty(mapping)) {
			return null;
		}

		const mappingParsed = JSON.parse(mapping);

		return getAllPaths(mappingParsed).map((path, index) => {
			const value = lod_.get(mappingParsed, path);
			const handlebarValue = lod_.get(mapObject, value);

			return (
				<RowMapping
					key={`${path}-${index}`}
					mappingIsObject={mappingIsObject}
					handlebarValue={handlebarValue}
					config={config}
					path={path}
					value={value}
					onChangePath={onChangePath}
					onChangeValue={onChangeValue}
					deleteRow={deleteRow}
					allPaths={getAllPaths(mappingParsed)}
				/>
			);
		});
	}, [
		mapping,
		mapObject,
		config,
		mappingIsObject,
		getAllPaths,
		onChangePath,
		onChangeValue,
		deleteRow
	]);

	// Affiche rien pendant le chargement initial
	if (loading === true) {
		return null;
	}

	return (
		<MDBox mt={2} bgColor="white" p={1} borderRadius="md" display="flex" flexDirection="column">
			<MDBox display="flex" flexDirection="column" alignItems="center" sx={{ mt: 1 }}>
				{/* ==================== BARRE D'OUTILS ==================== */}
				<MDBox
					display="flex"
					flexDirection="row"
					alignItems="center"
					flex="1"
					sx={{ width: "100%" }}
					style={{
						backgroundColor: "white",
						position: "sticky",
						zIndex: 2,
						top: 0
					}}
					pb={1}
				>
					{/* Aperçu du mapping reconstruit (lecture seule) */}
					<MDInput
						disabled={isEditing === false}
						fullWidth
						value={
							isEditing === true ? editedValue : buildMappingStringFromObject(mapObject, mapping)
						}
						onChange={e => setEditedValue(e.target.value)}
						InputProps={{
							endAdornment:
								isEditing === false ? (
									<InputAdornment position="end">
										<Tooltip placement="top" title={t("SETTINGS.DICTIONARY.copyToClipboard")}>
											<IconButton
												color="info"
												style={{ pointerEvents: "visible" }}
												onClick={handleCopyToClipboard}
											>
												<Icon>copy</Icon>
											</IconButton>
										</Tooltip>
									</InputAdornment>
								) : null
						}}
					/>
					{/* Boutons en mode édition */}
					{isEditing === true ? (
						<>
							<MDButton sx={{ ml: 1 }} variant="contained" color="info" onClick={handleSaveEdit}>
								{t("SETTINGS.save")}
							</MDButton>
							<MDButton sx={{ ml: 1 }} variant="outlined" color="info" onClick={handleCancelEdit}>
								{t("SETTINGS.cancel")}
							</MDButton>
						</>
					) : (
						<>
							{/* Bouton d'ajout d'une nouvelle ligne */}
							<MDButton
								sx={{ ml: 1 }}
								variant="contained"
								color="info"
								onClick={handleAddRow}
								disabled={mappingIsObject === false && lod_.isNil(mapping) === false}
							>
								{t("SETTINGS.add")}
							</MDButton>
							{/* Bouton d'édition manuelle */}
							<Tooltip placement="top" title={t("SETTINGS.edit")}>
								<IconButton size="small" color="secondary" sx={{ ml: 1 }} onClick={handleStartEdit}>
									<Icon>edit</Icon>
								</IconButton>
							</Tooltip>
						</>
					)}
				</MDBox>

				{/* ==================== LISTE DES LIGNES DE MAPPING ==================== */}
				{renderRows}

				{/* Ancre pour le scroll automatique */}
				<div ref={bottom} />
			</MDBox>
		</MDBox>
	);
};

export default MappingContent;
